import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleHeading,
    CollapsiblePanel,
    CollapsibleSubtitle,
    CollapsibleTitle,
    CollapsibleTrigger,
} from '@plato/ui/containers/collapsible';
import { Button } from '@plato/ui/primitives/button';
import { Typography } from '@plato/ui/primitives/typography';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { MoneyDeltaBadge } from '../../insights-common/components/money-delta-badge.tsx';
import { useInsightsWithAnalytics } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';

type LogInteractionInsightListProps = {
    customerId: string;
};

const maxVisiblePerGroup = 3;

function InsightRow({ insightData }: { insightData: InsightListItem }): ReactNode {
    const { t } = useTranslation();

    const { insight, details } = insightData;
    const insightTypeName = getInsightName(t, insight);
    const categoryName = details.category.title ?? insight.actionable_entity_id;
    const DirectionIcon =
        insight.insight_type === InsightType.CHURN_WARNING ? TrendingDownIcon : TrendingUpIcon;

    return (
        <Card>
            <CardFront>
                <CardStart>
                    <DirectionIcon />
                </CardStart>
                <CardContent>
                    <CardHeader className="flex-nowrap items-center">
                        <CardTitle className="min-w-0 flex-1 truncate">{insightTypeName}</CardTitle>
                        <CardSubtitle className="shrink-0 items-center tabular-nums">
                            <MoneyDeltaBadge>
                                {roundAbsToHundred(insight.absolute_number ?? 0)}
                            </MoneyDeltaBadge>
                        </CardSubtitle>
                    </CardHeader>
                    <CardDetail className="truncate text-center @md:text-left">{categoryName}</CardDetail>
                </CardContent>
            </CardFront>
        </Card>
    );
}

export function LogInteractionInsightList({ customerId }: LogInteractionInsightListProps): ReactNode {
    const { t } = useTranslation('notes');

    const { insights, isLoading } = useInsightsWithAnalytics({ customerId });

    const [showAllRisks, setShowAllRisks] = useState(false);
    const [showAllOpportunities, setShowAllOpportunities] = useState(false);

    const isEmpty = insights.length === 0;

    const risks = insights.filter((i) => i.insight.insight_type === InsightType.CHURN_WARNING);
    const opportunities = insights.filter((i) => i.insight.insight_type !== InsightType.CHURN_WARNING);

    const summaryParts = [
        { count: risks.length, label: t('risks', { ns: 'insights' }) },
        { count: opportunities.length, label: t('opportunities', { ns: 'insights' }) },
    ]
        .filter((part) => part.count > 0)
        .map((part) => `${part.count} ${part.label}`);

    return (
        <Collapsible>
            <CollapsibleHeader>
                <CollapsibleHeading>
                    <CollapsibleTitle>{t('notes__ai_insights_section_title')}</CollapsibleTitle>
                    {summaryParts.length > 0 && (
                        <CollapsibleSubtitle>{summaryParts.join(' · ')}</CollapsibleSubtitle>
                    )}
                </CollapsibleHeading>
                <CollapsibleTrigger />
            </CollapsibleHeader>
            <CollapsiblePanel>
                {isEmpty ? (
                    <p className="text-body text-content-weak">
                        {isLoading
                            ? t('notes__ai_insights_section_loading')
                            : t('notes__ai_insights_section_empty')}
                    </p>
                ) : (
                    <div className="flex flex-col gap-default">
                        {risks.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('risks', { ns: 'insights' })}
                                </Typography>
                                {(showAllRisks ? risks : risks.slice(0, maxVisiblePerGroup)).map(
                                    (insightData) => (
                                        <InsightRow key={insightData.insight.id} insightData={insightData} />
                                    ),
                                )}
                                {!showAllRisks && risks.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllRisks(true);
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: risks.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                        {opportunities.length > 0 && (
                            <div className="flex flex-col gap-cozy">
                                <Typography variant="subtle-heading">
                                    {t('opportunities', { ns: 'insights' })}
                                </Typography>
                                {(showAllOpportunities
                                    ? opportunities
                                    : opportunities.slice(0, maxVisiblePerGroup)
                                ).map((insightData) => (
                                    <InsightRow key={insightData.insight.id} insightData={insightData} />
                                ))}
                                {!showAllOpportunities && opportunities.length > maxVisiblePerGroup && (
                                    <Button
                                        variant="secondary"
                                        className="self-start"
                                        onClick={() => {
                                            setShowAllOpportunities(true);
                                        }}
                                    >
                                        {t('notes__ai_insights_show_more', {
                                            count: opportunities.length - maxVisiblePerGroup,
                                        })}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </CollapsiblePanel>
        </Collapsible>
    );
}
